import { DateTools } from './date-tools'
import { Draggable } from '@fullcalendar/interaction'

/**
 * Classe utilitaire pour gérer les événements du calendrier
 */
export class CalendarTools {
  /**
   * Vérifie si un événement a des duplications dans le calendrier
   */
  public static hasDuplicateEvents(event: any, calendarEvents: any[]): boolean {
    if (!event || !event.extendedProps || !event.extendedProps.productsData) {
      return false
    }

    const productIds = event.extendedProps.productsData.map((p: any) => p.id)
    const signature = DateTools.createProductSignature(productIds)

    return calendarEvents.some((existingEvent) => {
      if (
        !existingEvent.extendedProps ||
        !existingEvent.extendedProps.productsData
      ) {
        return false
      }

      const existingProductIds = existingEvent.extendedProps.productsData.map(
        (p: any) => p.id
      )
      const existingSignature =
        DateTools.createProductSignature(existingProductIds)

      return signature === existingSignature
    })
  }

  /**
   * Nettoie les événements dupliqués d'un calendrier
   */
  public static clearDuplicateEvents(calendarApi: any): void {
    if (!calendarApi) return

    const events = calendarApi.getEvents()
    const eventsBySignature: { [key: string]: any[] } = {}

    events.forEach((event: any) => {
      const productIds =
        event.extendedProps?.productsData?.map((p: any) => p.id) || []

      if (productIds.length > 0) {
        const signature = DateTools.createProductSignature(productIds)

        if (!eventsBySignature[signature]) {
          eventsBySignature[signature] = []
        }

        eventsBySignature[signature].push(event)
      }
    })

    // Pour chaque groupe d'événements avec la même signature, garder seulement le premier
    Object.values(eventsBySignature).forEach((duplicates) => {
      if (duplicates.length > 1) {
        for (let i = 1; i < duplicates.length; i++) {
          duplicates[i].remove()
        }
      }
    })
  }

  /**
   * Prépare les données d'un événement pour le calendrier
   */
  public static prepareEventData(productsData: any[], erpSource: string): any {
    if (
      !productsData ||
      !Array.isArray(productsData) ||
      productsData.length === 0
    ) {
      return null
    }

    // Assurer que chaque produit a la bonne source
    const products = productsData.map((product) => ({
      ...product,
      erpSource: product.erpSource || erpSource,
    }))

    const totalDurationMinutes = products.reduce(
      (sum, product) => sum + (product.product?.duration || 0),
      0
    )

    const productsNames =
      products
        .map((p) => p.product?.name || '')
        .join(', ')
        .substring(0, 60) + (products.length > 2 ? '...' : '')

    const productIds = products.map((p) => p.id)
    const eventId = DateTools.generateEventId(productIds)

    return {
      productsData: products,
      productsNames,
      totalDurationMinutes,
      productIds,
      eventId,
      title: productsNames || 'Événement',
    }
  }

  /**
   * Initialise un élément glissable pour le calendrier
   */
  public static initializeProductDraggable(
    container: HTMLElement,
    productsData: any[],
    onDragCallback: Function
  ): any {
    if (!container || !productsData) return null

    // S'assurer que les éléments sont marqués comme draggable
    const draggableItems = container.querySelectorAll(
      '.product-item, .product-group-item'
    )
    draggableItems.forEach((item) => {
      item.setAttribute('draggable', 'true')
    })

    // Utiliser directement le Draggable importé en haut du fichier
    try {
      return new Draggable(container, {
        itemSelector: '.product-item, .product-group-item',
        eventData: (el) => onDragCallback(el, productsData),
      })
    } catch (e) {
      console.error("Erreur lors de l'initialisation du draggable:", e)
      return null
    }
  }
}
